import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { REQUESTS_LIST_RQ_KEY } from '@/api/models/requests/hooks/useRequestsList'
import { useSelectedProjectStore } from '@/components/entities/common/model/selected-project.store'
import { PageType } from '@/components/entities/requests/model/types'
import { SelectedQuickFilters } from '@/components/widgets/request/FiltersPopover/types'

import { buildRequestsList } from '../lib/requests.mapper'
import { RequestsService } from '../lib/requests.service'
import { useRequestsStatuses } from './useGetRequestsStatuses'
import { useRequestTypes } from './useRequestTypes'

export const useRequestsSummary = (
  isPageType: PageType,
  allFilters?: SelectedQuickFilters
) => {
  const { selectedProjectId: projectId } = useSelectedProjectStore()
  const {
    status,
    data,
    error: requestsSummaryError,
    isLoading,
    isFetching,
    refetch,
    isError
  } = useQuery({
    queryKey: [REQUESTS_LIST_RQ_KEY, projectId, isPageType, allFilters],
    queryFn: () => {
      const payload = {
        projectId,
        page: 1,
        pageSize: 5,
        ...allFilters
      }

      return RequestsService.getRequests(payload)
    },
    enabled: Boolean(projectId && allFilters),
    placeholderData: keepPreviousData
  })

  const { data: requestStatuses, error: requestStatusesError } =
    useRequestsStatuses(true)

  const {
    data: types,
    arePassesAvailable,
    isLoading: isLoadingTypes,
    isFetching: isFetchingTypes
  } = useRequestTypes()

  const mappedData = useMemo(
    () => ({ data: buildRequestsList(data?.data, types, requestStatuses) }),
    [data?.data, requestStatuses, types]
  )

  return {
    isError,
    data: mappedData.data,
    refetch,
    error: requestsSummaryError || requestStatusesError,
    isLoading: isLoading || isLoadingTypes,
    isFetching: isFetching || isFetchingTypes,
    status,
    arePassesAvailable: arePassesAvailable || false
  }
}
